import { findStoreListing } from '../data/product/product-lookup.data';
import { Review } from '../model/review/review.model';
import { StorePublicReview } from '../service/store/store.service';

/**
 * The review bubbles' data (app-review-card), shared by the home page's review wall and
 * the bottom of every theme page (owner, 2026-10-04): the marketplaces' comments from the
 * monitor stats and the approved comments of buyers on codigrate.com, each bubble painted
 * from its theme's accent.
 */

/** the theme's accent for its bubbles (the All In One bundle has no single identity: Codigrate blue) */
function accentOf( product: any ): string {
  const isBundle = product?.isBundle || product?.key === 'all-in-one';
  const dt = product?.designTokens || {};
  return isBundle ? '#00BAFF' : ( dt.primaryColor || dt.editorForeground || '#5b8def' );
}

/** a marketplace's comments of one theme (the stats rows' comments[]), as bubbles */
export function marketplaceReviewCards( product: any, statsRows: any[] | null | undefined ): Review[] {
  const cards: Review[] = [];
  for ( const platformData of statsRows || [] ) {
    if ( !Array.isArray( platformData?.comments ) ) {
      continue;
    }
    for ( const c of platformData.comments ) {
      // bodies arrive as marketplace HTML (<p>…</p>, entities): plain text in a bubble
      const body = stripHtml( c?.body );
      if ( !body ) {
        continue;
      }
      const plat = reviewPlatform( platformData.platform );
      const accent = accentOf( product );
      cards.push( {
        reviewer: ( c.author || 'Anonymous' ).trim(),
        stars: Math.round( c.rating ) > 0 ? Math.round( c.rating ) : 0,
        review: body,
        date: formatReviewDate( c.commentedAt ),
        title: stripHtml( c.title ) || undefined,
        theme: product.name,
        themeLogo: product.logo,
        ...deriveReviewVariants( accent ),
        accent,
        avatar: ( c.avatar || c.authorAvatar || '' ).trim() || undefined,
        platform: platformData.platform,
        platformLabel: plat.label,
        platformLogo: plat.logo,
      } );
    }
  }
  return cards;
}

/** the approved comments of buyers on codigrate.com of one theme, as bubbles */
export function storeReviewCards( product: any, reviews: StorePublicReview[] | null | undefined ): Review[] {
  const accent = accentOf( product );
  return ( reviews || [] )
    .filter( r => r.product === product?.key && ( r.comment || '' ).trim() )
    .map( r => {
      const listing = findStoreListing( r.sku );
      return {
        reviewer: ( r.displayName || 'Verified Buyer' ).trim(),
        stars: r.rating,
        review: r.comment.trim(),
        date: formatReviewDate( r.date ),
        theme: product.name,
        themeLogo: product.logo,
        ...deriveReviewVariants( accent ),
        accent,
        platform: r.platform,
        platformLabel: listing?.platformName || r.platform,
        platformLogo: listing?.platformLogo || '',
      };
    } );
}

/** the approved comments of one web tool, as bubbles; no colors of their own, the wall paints them in the site's theme */
export function toolReviewCards( reviews: { rating: number; comment: string; displayName: string; date: string }[] | null | undefined,
                                 toolName: string ): Review[] {
  return ( reviews || [] )
    .filter( r => ( r.comment || '' ).trim() )
    .map( r => ( {
      reviewer: ( r.displayName || 'Codigrate User' ).trim(),
      stars: r.rating,
      review: r.comment.trim(),
      date: formatReviewDate( r.date ),
      theme: toolName,
    } ) );
}

/** marketplace HTML to trimmed plain text (tags out, whitespace folded, the common entities decoded) */
function stripHtml( raw: any ): string {
  if ( !raw ) {
    return '';
  }
  return String( raw )
    .replace( /<[^>]*>/g, ' ' )
    .replace( /&nbsp;/g, ' ' )
    .replace( /&amp;/g, '&' )
    .replace( /&lt;/g, '<' )
    .replace( /&gt;/g, '>' )
    .replace( /&quot;/g, '"' )
    .replace( /&#3?9;|&apos;/g, "'" )
    .replace( /\s+/g, ' ' )
    .trim();
}

/** a timestamp -> "Aug 2026"; blank if unparseable */
function formatReviewDate( raw: any ): string {
  if ( !raw ) {
    return '';
  }
  const d = new Date( raw );
  if ( isNaN( d.getTime() ) ) {
    return '';
  }
  return d.toLocaleDateString( 'en-US', { month: 'short', year: 'numeric' } );
}

/** a monitor platform key -> a label + the marketplace's logo */
function reviewPlatform( platform: string ): { label: string; logo: string } {
  switch ( platform ) {
    case 'jetbrains':
      return { label: 'JetBrains', logo: './assets/third-parties/logo/jetbrains.webp' };
    case 'open-vsx':
    case 'openVsx':
    case 'openvsx':
      return { label: 'Open VSX', logo: './assets/third-parties/logo/open-vsx.webp' };
    case 'vscode':
      return { label: 'VS Code', logo: './assets/third-parties/logo/vscode.webp' };
    case 'firefox':
      return { label: 'Firefox', logo: './assets/third-parties/logo/firefox.webp' };
    default:
      return { label: platform || 'Marketplace', logo: '' };
  }
}

/**
 * A light and a dark bubble variant from a theme's accent hue, so the bubble always matches
 * the site's mode: the hue is kept, saturation and lightness tuned for readable backgrounds
 * and text in each mode (Roraima's orange -> light peach-to-cream / deep brown).
 */
function deriveReviewVariants( accentHex: string ): {
  lightBgFrom: string; lightBgTo: string; lightInk: string; lightInkSoft: string;
  darkBgFrom: string; darkBgTo: string; darkInk: string; darkInkSoft: string;
} {
  const [ h, s ] = hexToHueSat( accentHex );
  const sat = ( cap: number ) => Math.min( s, cap );
  const hsl = ( ss: number, l: number ) => hslToHex( h, ss, l );
  return {
    lightBgFrom: hsl( sat( 62 ), 90 ),
    lightBgTo: hsl( sat( 42 ), 97 ),
    lightInk: hsl( sat( 48 ), 23 ),
    lightInkSoft: hsl( sat( 36 ), 43 ),
    darkBgFrom: hsl( sat( 42 ), 19 ),
    darkBgTo: hsl( sat( 52 ), 12 ),
    darkInk: hsl( sat( 26 ), 86 ),
    darkInkSoft: hsl( sat( 22 ), 66 ),
  };
}

/** hue (0-360) and saturation (0-100) of a hex color */
function hexToHueSat( hex: string ): [ number, number ] {
  const h = ( hex || '' ).replace( '#', '' );
  if ( h.length < 6 ) {
    return [ 210, 20 ];
  }
  const r = parseInt( h.substr( 0, 2 ), 16 ) / 255;
  const g = parseInt( h.substr( 2, 2 ), 16 ) / 255;
  const b = parseInt( h.substr( 4, 2 ), 16 ) / 255;
  const max = Math.max( r, g, b ), min = Math.min( r, g, b ), d = max - min;
  let hue = 0;
  if ( d ) {
    if ( max === r ) { hue = ( ( g - b ) / d ) % 6; }
    else if ( max === g ) { hue = ( b - r ) / d + 2; }
    else { hue = ( r - g ) / d + 4; }
    hue *= 60;
    if ( hue < 0 ) { hue += 360; }
  }
  const l = ( max + min ) / 2;
  const sat = d === 0 ? 0 : d / ( 1 - Math.abs( 2 * l - 1 ) );
  return [ Math.round( hue ), Math.round( sat * 100 ) ];
}

/** HSL (h 0-360, s/l 0-100) -> #rrggbb */
function hslToHex( h: number, s: number, l: number ): string {
  s /= 100; l /= 100;
  const c = ( 1 - Math.abs( 2 * l - 1 ) ) * s;
  const x = c * ( 1 - Math.abs( ( ( h / 60 ) % 2 ) - 1 ) );
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  if ( h < 60 ) { r = c; g = x; }
  else if ( h < 120 ) { r = x; g = c; }
  else if ( h < 180 ) { g = c; b = x; }
  else if ( h < 240 ) { g = x; b = c; }
  else if ( h < 300 ) { r = x; b = c; }
  else { r = c; b = x; }
  const to = ( val: number ) => Math.round( ( val + m ) * 255 ).toString( 16 ).padStart( 2, '0' );
  return '#' + to( r ) + to( g ) + to( b );
}
